import { Injectable } from '@angular/core';

import { Profile } from '../entities/profile.entity';
import { ProfileService } from './profile.service';
import { WebApiService } from './web-api.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly ACTIVATE_URL_PATH = '/api/v1.1/account/activate';
  private readonly CHANGE_PASSWORD_PATH = '/api/v1.1/account/changepassword';
  private readonly GET_ME_PATH = '/api/v1.1/account/me';

  private readonly FORGOT_PASSWORD_PATH = '/api/v1.1/account/forgot-password';
  private readonly RESET_PASSWORD_PATH = '/api/v1.1/account/reset-password';
  private readonly ONBOARDING_REQUEST_PATH =
    '/api/v1.1/account/onboarding-request';
  private readonly VERIFY_ONBOARDING_PATH =
    '/api/v1.1/account/verify-onboarding';

  private readonly CHANGE_EMAIL_PATH = '/api/v1.1/account/update-email';
  private readonly DELETE_ACCOUNT_PATH = '/api/v1.1/account/delete-account';

  constructor(
    private webApiService: WebApiService,
    private profileService: ProfileService
  ) {}

  /**
   * Activates account with credentials
   * @param email
   * @param password
   * @returns activation data
   */
  public activate(email: string, password: string): Promise<any> {
    const data = {
      email: email,
      password: password,
    };

    return new Promise<void>((resolve, reject) => {
      this.webApiService.put(this.ACTIVATE_URL_PATH, data).subscribe({
        next: (response) => {
          this.onAccountActivated(new Profile().deserialize(data));
          resolve();
        },
        error: (err) => reject(err),
      });
    });
  }

  /**
   * Sets profile with data
   * @param data
   */
  private onAccountActivated(data: Profile) {
    this.profileService.me = data;
  }

  /**
   * Changes password with actual password
   * @param oldPassword
   * @param newPassword
   * @returns success or fail data
   */
  public changePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<any> {
    const data = {
      oldPassword: oldPassword,
      newPassword: newPassword,
    };

    return new Promise<any>((resolve, reject) => {
      this.webApiService.post(this.CHANGE_PASSWORD_PATH, data).subscribe({
        next: (response) => resolve(response),
        error: (err) => reject(err),
      });
    });
  }

  /**
   * Changes user email
   * @param email
   * @returns profil with new email
   */
  public changeEmail(email: string): Promise<any> {
    const data = {
      email: email,
    };

    return new Promise<any>((resolve, reject) => {
      this.webApiService.post(this.CHANGE_EMAIL_PATH, data).subscribe({
        next: (response) => resolve(new Profile().deserialize(response.data)),
        error: (err) => reject(err),
      });
    });
  }

  /**
   * POST /account/forgot-password
   * Sends a message to the specified e-mail address to reset the password.
   * @param email
   * @param urlRedirection
   * @returns data after a email was sent
   */
  public forgotPassword(
    email: string,
    urlRedirection: string = ''
  ): Promise<any> {
    const data = {
      email: email,
      urlRedirection: urlRedirection,
    };

    return new Promise<any>((resolve, reject) => {
      this.webApiService.post(this.FORGOT_PASSWORD_PATH, data).subscribe({
        next: (response) => resolve(response),
        error: (err) => reject(err),
      });
    });
  }

  /**
   * POST /account/reset-password
   * Changes the password using a one-time-use token received by e-mail.
   * @param token
   * @param newPassword
   * @returns reset password
   */
  public resetPassword(token: string, newPassword: string): Promise<any> {
    const data = {
      token: token,
      newPassword: newPassword,
    };

    return new Promise<any>((resolve, reject) => {
      this.webApiService.post(this.RESET_PASSWORD_PATH, data).subscribe({
        next: (response) => resolve(response),
        error: (err) => reject(err),
      });
    });
  }

  /**
   * GET /account/me
   * Retrieves information about the logged account, using the authentication token.
   * @returns account of logged user
   */
  public getMe(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.webApiService.get(this.GET_ME_PATH).subscribe({
        next: (response) => {
          resolve(new Profile().deserialize(response.data));
        },
        error: (err) => reject(err),
      });
    });
  }

  /**
   * POST /account/delete-account
   * Deletes account
   * @param actualPassword
   * @returns deleted account adata
   */
  public deleteAccount(actualPassword: string = ''): Promise<any> {
    const data = {
      actualPassword: actualPassword,
    };

    return new Promise<any>((resolve, reject) => {
      this.webApiService.post(this.DELETE_ACCOUNT_PATH, data).subscribe({
        next: (response) => resolve(response),
        error: (err) => reject(err),
      });
    });
  }

  /**
   * POST /account/onboarding-request
   * Sends a message to retrieve access onboarding link
   * @param urlRedirection
   * @returns onboarding request data
   */
  public onboardingRequest(urlRedirection: string = ''): Promise<any> {
    const data = {
      urlRedirection: urlRedirection,
    };

    return new Promise<any>((resolve, reject) => {
      this.webApiService.post(this.ONBOARDING_REQUEST_PATH, data).subscribe({
        next: (response) => resolve(response),
        error: (err) => reject(err),
      });
    });
  }

  /**
   * POST /account/verify-onboarding
   * Verify onboarding token validation
   * @param token
   * @returns verify onboarding data
   */
  public verifyOnboarding(token: string): Promise<any> {
    const data = {
      token: token,
    };

    return new Promise<any>((resolve, reject) => {
      this.webApiService.post(this.VERIFY_ONBOARDING_PATH, data).subscribe({
        next: (response) => resolve(response),
        error: (err) => reject(err),
      });
    });
  }
}
